import React, { useCallback, useContext, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Field, Form } from 'react-final-form';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { useTranslation } from 'react-i18next';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import validations from 'validations';
import PasswordField from 'components/PasswordField';
import TextAreaField from 'components/TextAreaField';
import { Button } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ReCAPTCHA from 'react-google-recaptcha';
import env from 'env';
import AuthContext from 'providers/Auth/Auth.context';
import { ExtendedRequestAccess } from 'api/private-viewers';
import api from 'api';
import { useParams } from 'react-router-dom';
import ContactNumberField from 'components/ContactNumberField';
import EmailField from 'components/EmailField';

import './PrivateListingLandingForm.styles.scss';
import './PrivateListingLandingForm.styles.responsive.scss';

type PrivateListingLandingFormProps = {
  className?: string;
  onSuccess: () => void;
};

const PrivateListingLandingForm: React.FC<PrivateListingLandingFormProps> = (
  props,
) => {
  const { className, onSuccess } = props;

  const { isLoggedIn } = useContext(AuthContext);
  const { currentUser } = useContext(CurrentUserContext);
  const { popServerError, popError } = useContext(PopNotificationsContext);

  const { listingId } = useParams();

  const recaptchaRef = useRef(null);

  const readOnly = isLoggedIn;

  const commonValidations = useValidations();

  const { t } = useTranslation();

  const classes = classNames('private-listing-landing-form', className);

  const initialValues = useMemo(
    () =>
      !currentUser
        ? null
        : {
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            email: currentUser.email,
            linkedinProfile: currentUser.linkedinAccount
              ? currentUser.linkedinAccount
              : undefined,
          },
    [currentUser],
  );

  const onSubmit = useCallback(
    async (values: ExtendedRequestAccess) => {
      const { password, ...rest } = values;
      const recaptchaResponseToken = recaptchaRef.current?.getValue();

      if (!isLoggedIn && !recaptchaResponseToken) {
        popError(t('recaptchaRequired'));
        return;
      }

      try {
        isLoggedIn
          ? await api.privateViewers.requestPrivateListingAccessLoggedIn(
              listingId,
              rest,
            )
          : await api.privateViewers.requestPrivateListingAccess(listingId, {
              ...rest,
              password,
              recaptchaResponseToken,
            });
        onSuccess();
      } catch (e) {
        popServerError(e);
        recaptchaRef.current?.reset();
      }
    },
    [isLoggedIn, listingId, onSuccess, popError, popServerError, t],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, invalid, submitting }) => (
        <form onSubmit={handleSubmit} className={classes}>
          <div className="grouped">
            <Field
              name="firstName"
              label={t('firstName')}
              component={InputField}
              validate={commonValidations.name}
              required
              readOnly={readOnly}
            />
            <Field
              name="lastName"
              label={t('lastName')}
              component={InputField}
              validate={commonValidations.name}
              required
              readOnly={readOnly}
            />
          </div>

          <div className="grouped">
            <EmailField required readOnly={readOnly} />
            <ContactNumberField required />
          </div>

          <div className="grouped">
            <Field
              name="company"
              label={t('companyYouRepresent')}
              component={InputField}
              validate={validations.required()}
              required
            />
            <Field
              name="linkedinProfile"
              label={t('linkedin')}
              component={InputField}
              validate={validations.linkedinValidation()}
            />
          </div>

          <Field
            name="natureOfInterest"
            label={t('natureOfYourInterest')}
            placeholder={t('natureOfYourInterestPlaceholder')}
            component={TextAreaField}
            validate={validations.required()}
            maxLength={1000}
            required
            rows="4"
          />

          <Field
            name="howDiscoverListing"
            label={t('howDidYouDiscoverThisListing')}
            component={InputField}
            validate={validations.required()}
            maxLength={100}
            required
          />

          {!currentUser && (
            <>
              <div className="grouped">
                <Field
                  name="password"
                  label={t('password')}
                  component={PasswordField}
                  validate={commonValidations.password}
                  required
                />
                <span />
              </div>
              <div className="grouped">
                <ReCAPTCHA ref={recaptchaRef} sitekey={env.ReCAPTCHA_KEY} />
                <span />
              </div>
            </>
          )}

          <Button type="submit" disabled={invalid || submitting}>
            {t('registerInterestInThisListing')}
          </Button>
        </form>
      )}
    />
  );
};

export default PrivateListingLandingForm;
